/**
 * Frontend error-report client — Issue #178.
 *
 * The shared utility used by `ErrorBoundary` (and future client surfaces) to
 * forward a sanitized error capture to the backend `/api/v1/error-reports`
 * endpoint via the generated typed client.
 *
 * 红线：
 *   - 不允许在前端使用 stdout / stderr API 替代后端上报。
 *   - 不允许把原始 `error.stack` 字符串发送给后端：堆栈可能携带 token、手机号或
 *     其他 PII。客户端只发送脱敏摘要，后端会再做一次 sanitize。
 *   - 凡是与后端通信的代码必须经过 generated `@jinzhuan/api-client`；不允许手写
 *     fetch / XMLHttpRequest。
 */
import { createApiClient } from '@/lib/adminApiClient';
import type { CreateErrorReportRequest, ErrorReportDto } from '@/lib/adminApiClient';

const MESSAGE_MAX = 480;
const ERROR_NAME_MAX = 100;
const ROUTE_MAX = 200;

const PII_PATTERNS: ReadonlyArray<{ pattern: RegExp; replacement: string }> = [
  { pattern: /\b1[3-9]\d{9}\b/g, replacement: '[redacted-phone]' },
  { pattern: /\b\d{17}[\dXx]\b/g, replacement: '[redacted-id]' },
  { pattern: /\b\d{15}\b/g, replacement: '[redacted-id]' },
  {
    pattern: /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g,
    replacement: '[redacted-token]',
  },
  { pattern: /Bearer\s+[A-Za-z0-9._-]+/g, replacement: 'Bearer [redacted-token]' },
  {
    pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
    replacement: '[redacted-email]',
  },
];

const mask = (raw: string): string => {
  let out = raw;
  for (const rule of PII_PATTERNS) {
    out = out.replace(rule.pattern, rule.replacement);
  }
  return out;
};

const cap = (raw: string, max: number): string =>
  raw.length <= max ? raw : raw.slice(0, max) + '…';

export interface BuildErrorReportInput {
  readonly error: unknown;
  /** Component / boundary that captured the error. */
  readonly componentName?: string;
  /** Current route (pathname only, no query — query may carry tokens). */
  readonly route?: string;
  /** Frontend release id (git short sha) — sourced from build-time env. */
  readonly releaseId?: string;
}

export interface BuiltErrorReport extends CreateErrorReportRequest {}

/**
 * Build the request payload from a captured error. Strips stack frames and
 * masks PII before the bytes ever leave the page; the backend re-sanitizes
 * defensively but we want defence in depth.
 */
export const buildErrorReportPayload = (
  input: BuildErrorReportInput,
): BuiltErrorReport => {
  const errorObj = input.error instanceof Error ? input.error : null;
  const rawName = errorObj?.name ?? (typeof input.error === 'string' ? 'Error' : 'Error');
  const rawMessage = errorObj?.message ?? (typeof input.error === 'string' ? input.error : 'unknown-error');

  const safeRoute = input.route ? cap(input.route.split('?')[0] ?? '', ROUTE_MAX) : undefined;

  const base = {
    source: 'admin_web' as const,
    severity: 'error' as const,
    errorName: cap(rawName.replace(/[^\w.:-]+/g, '') || 'Error', ERROR_NAME_MAX),
    errorMessage: cap(mask(rawMessage), MESSAGE_MAX),
    occurredAt: new Date().toISOString(),
  };
  return {
    ...base,
    ...(safeRoute ? { route: safeRoute } : {}),
    ...(input.releaseId ? { releaseId: input.releaseId } : {}),
    ...(input.componentName ? { context: { componentName: input.componentName } } : {}),
  };
};

export interface ErrorReportClient {
  report(input: BuildErrorReportInput): Promise<void>;
}

export interface CreateErrorReportClientOptions {
  /** Resolved API base URL — must align with the main api client. */
  readonly baseUrl: string;
  /** Returns the current access token, or null when anonymous. */
  readonly getAccessToken?: () => string | null | undefined | Promise<string | null | undefined>;
  /**
   * Optional capped callback so callers (tests, retry queues) can observe
   * report outcomes without going through the network layer. The boundary
   * never throws back into React render.
   */
  readonly onResult?: (outcome: { ok: true; dto: ErrorReportDto } | { ok: false; error: unknown }) => void;
  /** Inject a custom fetch implementation (e.g. for tests). */
  readonly fetch?: typeof fetch;
}

/**
 * Build a client that POSTs sanitized error reports. Failure is swallowed:
 * an error inside the error boundary must never propagate back into React
 * and trigger a render loop. We forward outcomes to the optional `onResult`
 * hook so a future toast / retry queue can observe them without changing
 * this contract.
 */
export const createErrorReportClient = (
  opts: CreateErrorReportClientOptions,
): ErrorReportClient => {
  const client = createApiClient({
    baseUrl: opts.baseUrl,
    ...(opts.getAccessToken ? { getAccessToken: opts.getAccessToken } : {}),
    ...(opts.fetch ? { fetch: opts.fetch } : {}),
  });
  return {
    async report(input) {
      const payload = buildErrorReportPayload(input);
      try {
        const { data, error } = await client.POST('/api/v1/error-reports', { body: payload });
        if (error) {
          opts.onResult?.({ ok: false, error });
          return;
        }
        if (data) {
          opts.onResult?.({ ok: true, dto: data });
        }
      } catch (err) {
        opts.onResult?.({ ok: false, error: err });
      }
    },
  };
};
